import { useEffect, useRef } from 'react';

export type HeadTag = {
    tag: 'link' | 'meta';
    attrs: Record<string, string>;
};

/** Marque nos balises, pour ne retirer que les nôtres au démontage. */
const OWNED_ATTR = 'data-unboared-head';

/**
 * Injecte des balises dans `<head>` le temps où le composant est monté, et les
 * retire au démontage.
 *
 * Remplace `react-helmet`, **inerte** dans cette app : il se monte sans jamais
 * toucher au `<head>` (React 18 ; constaté le 10/09/2026 en montant AnimatorLayout
 * sur une route de test — ni manifest, ni theme-color, ni apple-touch-icon). Cf
 * gotcha #154. Pas de nouvelle dépendance : `react-helmet-async` est lui-même en
 * fin de vie, pour deux usages ça ne vaut pas le coût.
 *
 * `tags` peut être recréé à chaque render : l'effet se rejoue sur le *contenu* du
 * tableau, pas sur son identité.
 */
export function useHeadTags(tags: HeadTag[]) {
    const key = JSON.stringify(tags);
    const tagsRef = useRef(tags);
    tagsRef.current = tags;

    useEffect(() => {
        const created = tagsRef.current
            .filter(({ tag, attrs }) => {
                // Une balise identique déjà posée par index.html (le manifest sur une
                // arrivée directe /animator) ne doit pas être dupliquée — et surtout
                // pas retirée au démontage : elle ne nous appartient pas.
                const selector =
                    tag +
                    Object.entries(attrs)
                        .map(([n, v]) => `[${n}="${CSS.escape(v)}"]`)
                        .join('');
                return !document.head.querySelector(selector);
            })
            .map(({ tag, attrs }) => {
                const el = document.createElement(tag);
                Object.entries(attrs).forEach(([name, value]) => el.setAttribute(name, value));
                el.setAttribute(OWNED_ATTR, '');
                document.head.appendChild(el);
                return el;
            });
        return () => created.forEach((el) => el.remove());
    }, [key]);
}
